import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * <InternalAd slot="home_after_hero" variant="banner" />
 *
 * Fetches a single KARM-controlled promotional ad for the given slot. Renders
 * nothing if no ad is configured for the slot — the surface stays clean.
 *
 * Two visual variants:
 *   - "banner" — horizontal card (default)
 *   - "compact" — narrow vertical card (for sidebars)
 *
 * Click tracking: /api/ads/click/{ad_id} fires in the background; the CTA URL
 * opens immediately so the user never waits.
 * Impressions: tracked server-side when the ad payload is served.
 */
export default function InternalAd({ slot, variant = "banner", className = "" }) {
  const [ad, setAd] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/ads/slot/${slot}`)
      .then((r) => alive && setAd(r.data?.ad || null))
      .catch(() => alive && setAd(null));
    return () => { alive = false; };
  }, [slot]);

  if (dismissed || !ad) return null;

  const onClick = (e) => {
    e.preventDefault();
    api.post(`/ads/click/${ad.ad_id}`).catch(() => {});
    if (ad.cta_url) window.open(ad.cta_url, "_blank", "noopener,noreferrer");
  };

  const isCompact = variant === "compact";

  return (
    <aside
      ref={ref}
      className={
        "relative border transition-colors " +
        (isCompact ? "p-4 " : "p-5 md:p-6 ") + className
      }
      style={{ borderColor: "var(--karm-paper-edge)", background: "var(--karm-ivory-warm)" }}
      data-testid={`internal-ad-${slot}`}
      data-ad-id={ad.ad_id}
      aria-label="KARM promoted"
    >
      <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--karm-ink-muted)" }}>
        Promoted by KARM
      </div>
      <a href={ad.cta_url || "#"} onClick={onClick} className="block group">
        {ad.image_url && !isCompact && (
          <img
            src={ad.image_url}
            alt=""
            loading="lazy"
            className="mb-3 w-full max-h-40 object-cover rounded-sm"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        )}
        <h3
          className={"font-serif leading-snug group-hover:underline " + (isCompact ? "text-base" : "text-lg md:text-xl")}
          style={{ color: "var(--karm-ink)" }}
        >
          {ad.headline}
        </h3>
        {ad.body && (
          <p className={"font-serif mt-1.5 leading-relaxed " + (isCompact ? "text-xs" : "text-sm")}
             style={{ color: "var(--karm-ink-muted)" }}>
            {ad.body}
          </p>
        )}
        <div className="small-caps mt-3 inline-flex items-center gap-1" style={{ color: "var(--karm-vermillion)" }}>
          {ad.cta_label || "Learn more"} →
        </div>
      </a>
      <button
        type="button"
        aria-label="Dismiss ad"
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center text-xs opacity-40 hover:opacity-80"
        style={{ color: "var(--karm-ink-muted)" }}
        data-testid={`internal-ad-${slot}-dismiss`}
      >
        ✕
      </button>
    </aside>
  );
}
